import axios, { AxiosError, AxiosResponse } from 'axios'

import { TokenStorage } from './token-storage'
import { Http } from '../interfaces/http'
import toastService from '@/components/toast/toast-service'
import { routeNames } from '@/configs/routePaths'

export const API_URL = process.env.VITE_APP_API_SENSEDIA_DOMAIN_ACCESS_URL

export class DomainAccessFetcher implements Http {
  private readonly handleRequest = async (config: any) => {
    const TOKEN = TokenStorage.get()
    const AUTHORIZATION_CODE = TokenStorage.getAuthorizationCode()

    config.headers.Authorization = `bearer ${TOKEN}`
    config.headers['access_token'] = AUTHORIZATION_CODE

    return config
  }

  private readonly httpAxios = axios.create({
    baseURL: API_URL,
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Headers': '*',
      client_id: process.env.VITE_API_SENSEDIA_CLIENT_ID
    }
  })

  constructor() {
    this.httpAxios.interceptors.request.use(this.handleRequest)
    this.httpAxios.interceptors.response.use(
      this.handleResponse,
      this.handleError
    )
  }

  private readonly handleResponse = (response: AxiosResponse) => {
    if (response.data.message) {
      toastService.success(response.data.message)
    }
    return response
  }

  private readonly handleRedirectToLogin = (error: AxiosError) => {
    try {
      const errorMessage = error?.response?.data as string;
      return !errorMessage.toLowerCase().includes('Acesso VR inválido'.toLowerCase());
    }
    catch {
      return true;
    }
  }

  private readonly handleError = (error: AxiosError) => {
    switch (error.request.status) {
      case 401:
        const isRedirectToLogin = this.handleRedirectToLogin(error);
        if (isRedirectToLogin) {
          toastService.error('Sessão expirada. Faça login novamente')
          window.location.href = routeNames.login
          TokenStorage.logout()
        }
        break
      case 500:
        toastService.error('Error interno')
        break
      default:
        break
    }

    return Promise.reject(error)
  }

  async get<T>(
    path: string,
    params?: Record<string, any>,
    config?: any
  ): Promise<T> {
    const { data } = await this.httpAxios
      .get(path, { ...config, params })
      .catch(error => {
        const errorDetails = error?.response?.data?.erros?.pop() ?? 'Solicitação não realizada!'
        return Promise.reject(errorDetails as Error)
      })
    return data as T
  }

  async post<T>(
    path: string,
    params?: Record<string, any>,
    config?: any
  ): Promise<T> {
    try {
      const response = await this.httpAxios.post(path, { ...params }, { ...config });
      return response.data as T;
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      const formattedError = {
        message:
          errorResponse?.message ??
          errorResponse?.errors?.[0] ?? // pega a primeira mensagem genérica
          errorResponse ??
          'Solicitação não realizada!',
        errors: errorResponse?.errors ?? [],
        status: error?.response?.status
      };

      return Promise.reject(formattedError);
    }
  }

  async put<T>(
    path: string,
    params?: Record<string, any>,
    config?: any
  ): Promise<T> {
    const response = await this.httpAxios
      .put(path, { ...params }, { ...config })
      .catch(error => {
        const errorDetails = error?.response?.data?.erros?.pop() ?? 'Solicitação não realizada!'
        return Promise.reject(errorDetails as Error)
      })
    return response.data as T
  }

  async patch<T>(
    path: string,
    params?: Record<string, any>,
    config?: any
  ): Promise<T> {
    try {
      const response = await this.httpAxios.patch(path, { ...params }, { ...config });
      return response.data as T;
    } catch (error: any) {
      const errorResponse = error?.response?.data;

      const formattedError = {
        message:
          errorResponse?.message ??
          errorResponse?.errors?.[0] ??
          errorResponse?.erros?.[0] ??
          'Solicitação não realizada!',
        errors: errorResponse?.errors ?? errorResponse?.erros ?? [],
        fieldErrors: errorResponse?.fieldErrors ?? [],
        status: error?.response?.status
      };

      return Promise.reject(formattedError);
    }
  }

  async delete<T>(path: string, params?: any, config?: any): Promise<T> {
    const response = await this.httpAxios
      .delete(path, { ...config, params })
      .catch(error => {
        const errorDetails = error?.response?.data?.erros?.pop() ?? 'Solicitação não realizada!'
        return Promise.reject(errorDetails as Error)
      })
    return response.data as T
  }
}

export const API_DOMAIN_ACCESS = new DomainAccessFetcher()
